/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import '../Transfer.styles.scss';
import TransferStatusCell from './TransferStatusCell';
import TransferIdCell from './TransferIdCell';
import TransferEventIdCell from './TransferEventIdCell';
import SourceAccountCell from './SourceAccountCell';
import { readSortFromStorage, writeSortToStorage } from '../../../../util/listSortStorage';
import { TRANSFER_SORT_COLUMNS } from '../../../../util/constants';

const COLUMN_PROPERTIES = {
  ORDER_NUMBER: 'orderNumber',
  EVENT_ID: 'eventId',
  TYPE: 'type',
  STATUS: 'status',
  FAILURE_REASON: 'failureReason',
  SOURCE_ACCOUNT: 'sourceAccount',
  ACCOUNT: 'account',
  SALES_REP_NAME: 'salesRepName',
  TERRITORY_NUMBER: 'territoryNumber',
  USER_HOLD_REASONS: 'userHoldReasons',
  CREATED_DATE: 'createdDate',
} as const;

// Map display column properties to backend field names (for sort)
export const TRANSFER_COLUMN_TO_BACKEND_FIELD_MAP: { [key: string]: string } = {
  sourceAccount: 'accountName',
  account: 'receivingRepAccountName',
  accountName: 'accountName',
  accountNumber: 'accountName',
};

const GRAPHQL_TYPENAME = '__typename';

const TRANSFER_TYPES = {
  SENT: 'Sent',
  RECEIVED: 'Received',
  FAILED: 'Failed',
  COMPLETED: 'Completed',
} as const;

interface Props {
  type: string;
  data: any[];
  sortOrderList: (list: any) => void;
  sortedcolumn?: (property: any) => void;
  getTransferList?: any;
  selectedFilter?: any;
  offsetData?: any;
}

interface Sort {
  sortColumn: string;
  sortColumnDirection: {
    [key: string]: string;
  };
}

// CustomDataTableCell must have the same displayName as DataTableCell or it will be ignored.
TransferIdCell.displayName = DataTableCell.displayName;
TransferEventIdCell.displayName = DataTableCell.displayName;
TransferStatusCell.displayName = DataTableCell.displayName;
SourceAccountCell.displayName = DataTableCell.displayName;

const TransferDataTable: FC<Props> = ({
  data,
  type,
  sortedcolumn,
  getTransferList,
  selectedFilter,
}) => {
  const [sortObj, setSortObj] = useState<Sort | undefined>(() => {
    const stored = readSortFromStorage(
      `transfer_${type.toLowerCase()}`,
      TRANSFER_SORT_COLUMNS
    );
    return stored
      ? {
          sortColumn: stored.sortColumn,
          sortColumnDirection: {
            [stored.sortColumn]: stored.sortDirection,
          },
        }
      : undefined;
  });

  // Re-derive sortObj when type changes to handle tab switches
  React.useEffect(() => {
    const stored = readSortFromStorage(
      `transfer_${type.toLowerCase()}`,
      TRANSFER_SORT_COLUMNS
    );
    setSortObj(
      stored
        ? {
            sortColumn: stored.sortColumn,
            sortColumnDirection: {
              [stored.sortColumn]: stored.sortDirection,
            },
          }
        : undefined
    );
  }, [type]);

  const getFilterApiValue = (filterValues: any): any => {
    let reducedFilterValues = {};
    if (filterValues) {
      Object.keys(filterValues).forEach((key: string) => {
        if (Array.isArray(filterValues[key])) {
          const values = filterValues[key].map((item: any) => item.id);

          if (values && values.length > 0) {
            reducedFilterValues = { ...reducedFilterValues, [key]: [...values] };
          }
        } else if (Array.isArray(filterValues[key]) && filterValues[key]) {
          if (filterValues[key].length > 0) {
            reducedFilterValues = { ...reducedFilterValues, [key]: filterValues[key] };
          }
        } else if (key !== GRAPHQL_TYPENAME && filterValues[key]) {
          reducedFilterValues = { ...reducedFilterValues, [key]: filterValues[key] };
        }
      });

      return { ...reducedFilterValues };
    }
    return {};
  };
  const handleSort = (sortColumn: any): void => {
    const sortProperty = sortColumn.property;
    const { sortDirection } = sortColumn;

    const backendSortField = TRANSFER_COLUMN_TO_BACKEND_FIELD_MAP[sortProperty] || sortProperty;
    const mappedSortColumn = {
      property: sortProperty,
      sortDirection,
    };

    const sort = {
      sortColumn: sortProperty,
      sortColumnDirection: {
        [sortProperty]: sortDirection,
      },
    };
    if (sortedcolumn) {
      sortedcolumn(mappedSortColumn);
    }
    getTransferList({
      variables: {
        limit: 50,
        offset: 0,
        type: type.toUpperCase(),
        orderBy: backendSortField,
        orderSortType: sortDirection,
        ...(selectedFilter && { filters: getFilterApiValue(selectedFilter.filterValues) }),
      },
    });

    setSortObj(sort);
    writeSortToStorage(`transfer_${type.toLowerCase()}`, sortProperty, sortDirection);
  };

  return (
    <DataTable
      assistiveText={{
        columnSort: 'sort this column',
        columnSortedAscending: 'asc',
        columnSortedDescending: 'desc',
      }}
      items={data || []}
      id="Transfer-DataTable"
      className="font-12"
      fixedLayout
      keyboardNavigation
      joined
      onSort={handleSort}
    >
      {[
        <DataTableColumn
          key={COLUMN_PROPERTIES.ORDER_NUMBER}
          label="Transfer"
          property={COLUMN_PROPERTIES.ORDER_NUMBER}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.ORDER_NUMBER}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.ORDER_NUMBER]}
        >
          <TransferIdCell transferType={type} />
        </DataTableColumn>,
        <DataTableColumn
          key={COLUMN_PROPERTIES.EVENT_ID}
          label="Event"
          property={COLUMN_PROPERTIES.EVENT_ID}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.EVENT_ID}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.EVENT_ID]}
        >
          <TransferEventIdCell transferType={type} />
        </DataTableColumn>,
        <DataTableColumn
          key={COLUMN_PROPERTIES.TYPE}
          label="Transfer Type"
          property={COLUMN_PROPERTIES.TYPE}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.TYPE}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.TYPE]}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTIES.STATUS}
          label="Status"
          property={COLUMN_PROPERTIES.STATUS}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.STATUS}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.STATUS]}
        >
          <TransferStatusCell />
        </DataTableColumn>,
        type === TRANSFER_TYPES.FAILED && (
          <DataTableColumn
            key={COLUMN_PROPERTIES.FAILURE_REASON}
            label="Failure Reason"
            property={COLUMN_PROPERTIES.FAILURE_REASON}
            isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.FAILURE_REASON}
            sortable
            sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.FAILURE_REASON]}
          />
        ),
        <DataTableColumn
          key={COLUMN_PROPERTIES.SOURCE_ACCOUNT}
          label="Source Account"
          property={COLUMN_PROPERTIES.SOURCE_ACCOUNT}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.SOURCE_ACCOUNT}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.SOURCE_ACCOUNT]}
        >
          <SourceAccountCell />
        </DataTableColumn>,
        <DataTableColumn
          key={COLUMN_PROPERTIES.ACCOUNT}
          label="Destination Account"
          property={COLUMN_PROPERTIES.ACCOUNT}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.ACCOUNT}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.ACCOUNT]}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTIES.SALES_REP_NAME}
          label="Sales Rep"
          property={COLUMN_PROPERTIES.SALES_REP_NAME}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.SALES_REP_NAME}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.SALES_REP_NAME]}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTIES.TERRITORY_NUMBER}
          label="Territory"
          property={COLUMN_PROPERTIES.TERRITORY_NUMBER}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.TERRITORY_NUMBER}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.TERRITORY_NUMBER]}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTIES.USER_HOLD_REASONS}
          label="Hold Reason"
          property={COLUMN_PROPERTIES.USER_HOLD_REASONS}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.USER_HOLD_REASONS}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.USER_HOLD_REASONS]}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTIES.CREATED_DATE}
          label="Created Date"
          property={COLUMN_PROPERTIES.CREATED_DATE}
          isSorted={sortObj?.sortColumn === COLUMN_PROPERTIES.CREATED_DATE}
          sortable
          sortDirection={sortObj?.sortColumnDirection[COLUMN_PROPERTIES.CREATED_DATE]}
        />,
      ]}
    </DataTable>
  );
};

export default TransferDataTable;
